'use client';

/**
 * CartGiftsSelectorWidgetContents
 *
 * Contents for cart gift selection bottom sheet.
 * Matches mobile implementation pattern using SectionList pattern with:
 * - "Quà đã chọn" section (if any gifts selected)
 * - "Available gifts" section
 *
 * Uses useCartGiftSelection hook for cart-level gift selection state.
 */

import React, { type ReactNode, useCallback, useMemo, useEffect, type FC } from 'react';
import { observer, useSelector } from '@legendapp/state/react';
import { computed } from '@legendapp/state';
import type { CartGiftsSelectorWidgetContentsProps } from './cart-gifts-selector-widget.types';
import type { FreeGiftsData } from '../../../../types/front.graphql';
import { ProductGiftContents } from '../product-gift-contents';
import { SelectedGifts } from '../selected-gifts';
import { useCartGiftSelection } from '../../hooks/use-cart-gift-selection.hook';
import type { SelectedGift } from '../../types/gift-selection.types';
import styles from './cart-gifts-selector-widget.module.css';
import { Divider, TextIcon, GiftBox } from '@kidslabs/mario-nextjs-react-ui-core';

export interface CartGiftBottomSheetProps {
    cartLevelGiftRules: CartGiftsSelectorWidgetContentsProps['cartLevelGiftRules'];
    footer?: ReactNode;
    draftSelectedGifts$: CartGiftsSelectorWidgetContentsProps['draftSelectedGifts$'];
}

/**
 * CartGiftsSelectorWidgetContents - Contents for cart gift selection modal
 * Matches mobile's SectionList pattern
 */
const CartGiftsSelectorWidgetContentsInner: FC<CartGiftBottomSheetProps> = observer(
    ({ cartLevelGiftRules, footer, draftSelectedGifts$ }) => {
        // Convert CartLevelGiftRule[] to FreeGiftsData[] for compatibility with UI components
        // Using type assertion through unknown to bypass strict type checking since we're mapping to a simplified structure
        const sortedGiftsDataList = useMemo(() => {
            return cartLevelGiftRules.map(rule => ({
                __typename: 'FreeGiftsData' as const,
                products: rule.available_gifts
                    .filter((gift): gift is NonNullable<typeof gift> => gift !== null && gift.product != null)
                    .map(gift => ({
                        ...gift.product,
                        // Carry max_qty so GiftItem uses it as the gift quantity
                        maxQuantity: gift.max_qty,
                    })),
                rule: {
                    __typename: 'Rule' as const,
                    rule_id: rule.rule_id,
                    name: rule.rule_name ?? rule.rule_label ?? '',
                    description: rule.description,
                    discount_amount: rule.max_selections,
                },
            })) as unknown as FreeGiftsData[];
        }, [cartLevelGiftRules]);

        // Use cart-specific gift selection hook
        const cartGiftSelection = useCartGiftSelection({
            createNewSession: false,
        });

        // Get sessionId from cart gift selection store - declare early for use in effect
        const sessionId = cartGiftSelection.store$.sessionId.peek() ?? '';

        // Sync existing selected gifts from server into bottom-sheet draft state on mount.
        // IMPORTANT: Merge strategy — only replace gifts from the rules present
        // in cartLevelGiftRules (which may be filtered to a single rule for
        // "Đổi quà"). Gifts from other rules are kept untouched.
        useEffect(() => {
            const selectedGiftsFromServer = cartLevelGiftRules.flatMap(rule =>
                (rule.selected_gifts ?? [])
                    .filter((gift): gift is NonNullable<typeof gift> => gift !== null)
                    .map(gift => {
                        // Find the product info from available_gifts
                        const giftProduct = rule.available_gifts
                            .filter((p): p is NonNullable<typeof p> => p !== null)
                            .find(p => p?.product?.sku === gift.sku);

                        const product = giftProduct?.product;

                        return {
                            id: `${rule.rule_id}-${gift.sku}-1`,
                            ruleId: rule.rule_id,
                            productId: product?.id ?? 0,
                            selectionRound: 1,
                            sessionId: sessionId,
                            item: {
                                ...product,
                                maxQuantity: giftProduct?.max_qty,
                            },
                            giftData: {
                                __typename: 'FreeGiftsData' as const,
                                products: product ? [product] : [],
                                rule: {
                                    __typename: 'Rule' as const,
                                    rule_id: rule.rule_id,
                                    name: rule.rule_name ?? rule.rule_label ?? '',
                                },
                            },
                            quantity: gift.quantity,
                            selectedAt: Date.now(),
                            giftSlots: gift.quantity,
                        } as unknown as SelectedGift;
                    })
            );

            let singleGift: SelectedGift[] = [];
            if (selectedGiftsFromServer.length > 0) {
                singleGift = [selectedGiftsFromServer[0]];
            }
            draftSelectedGifts$.set(singleGift);
        }, [cartLevelGiftRules, draftSelectedGifts$, sessionId]);

        // Calculate total available gift slots from all visible rules
        const totalGiftSlots = useMemo(() => {
            return cartLevelGiftRules.reduce((total, rule) => {
                return total + rule.max_selections;
            }, 0);
        }, [cartLevelGiftRules]);

        // Visible rule IDs — the rules this bottom sheet instance covers
        const visibleRuleIds = useMemo(
            () => new Set(cartLevelGiftRules.map(rule => rule.rule_id)),
            [cartLevelGiftRules]
        );

        // Computed observable: only gifts belonging to visible rules.
        // This is what the bottom sheet UI displays and edits.
        const visibleSelectedGifts$ = useMemo(
            () =>
                computed(() => {
                    const allGifts = draftSelectedGifts$.get() ?? [];
                    return allGifts.filter((g: SelectedGift) => visibleRuleIds.has(g.ruleId ?? 0));
                }),
            [draftSelectedGifts$, visibleRuleIds]
        );

        const onSelectGift = useCallback(
            (gift: SelectedGift) => {
                draftSelectedGifts$.set([gift]);
            },
            [draftSelectedGifts$]
        );

        const onRemoveGift = useCallback(
            (giftId: string) => {
                const currentGifts = draftSelectedGifts$.peek() ?? [];
                draftSelectedGifts$.set(currentGifts.filter((gift: SelectedGift) => gift.id !== giftId));
            },
            [draftSelectedGifts$]
        );

        const onUpdateQuantity = useCallback(
            (giftId: string, quantity: number) => {
                const currentGifts = draftSelectedGifts$.peek() ?? [];
                draftSelectedGifts$.set(
                    currentGifts.map((gift: SelectedGift) =>
                        gift.id === giftId ? { ...gift, quantity, giftSlots: quantity } : gift
                    )
                );
            },
            [draftSelectedGifts$]
        );

        // Render section header - matches mobile pattern with TextIcon and Divider
        const renderSectionHeader = useCallback((section: string, selectedGift: boolean) => {
            return (
                <div className={selectedGift ? styles.sectionHeaderSelected : styles.sectionHeader}>
                    {!selectedGift && <Divider className={styles.divider} />}

                    <TextIcon
                        leftIcon={selectedGift ? <GiftBox width={16} height={16} /> : undefined}
                        text={section}
                        fontWeight={selectedGift ? 'bold' : 'normal'}
                        fontSize={selectedGift ? 'medium' : 'small'}
                        fontColor="var(--color-primary-100)"
                        containerClassName={
                            selectedGift ? styles.sectionTitleContainerSelected : styles.sectionTitleContainer
                        }
                    />

                    {selectedGift && <Divider className={styles.dividerSelected} />}
                </div>
            );
        }, []);

        // Build sections matching mobile's SectionList pattern
        const sections = [];

        // Section 1: Selected gifts for VISIBLE rules only
        const visibleSelectedGifts = useSelector(() => visibleSelectedGifts$.get() ?? []);
        if (visibleSelectedGifts.length > 0) {
            sections.push({
                title: 'Quà đã chọn',
                selectedGift: true,
                renderItem: () => (
                    <SelectedGifts
                        key={visibleSelectedGifts[0]?.id}
                        product={undefined}
                        selectedGifts$={visibleSelectedGifts$}
                        onRemoveGift={onRemoveGift}
                        onUpdateQuantity={onUpdateQuantity}
                        sessionId={sessionId}
                    />
                ),
            });
        }

        // Section 2: Available gifts for visible rules
        if (sortedGiftsDataList?.length > 0) {
            sections.push({
                title: 'Đơn hàng của bạn thỏa mãn các điều kiện tặng quà, vui lòng chọn thêm các quà tặng sau:',
                selectedGift: false,
                renderItem: () => (
                    <ProductGiftContents
                        counterValue={totalGiftSlots}
                        selectedGifts$={visibleSelectedGifts$}
                        sortedGiftsDataList={sortedGiftsDataList}
                        showOnlyRemainingGifts={false}
                        onSelectGift={onSelectGift}
                        sessionId={sessionId}
                    />
                ),
            });
        }

        return (
            <div className={styles.contentsWrapper}>
                <div className={styles.contentsContainer}>
                    {sections.map((section, index) => (
                        <div key={`section-${index}`} className={styles.section}>
                            {renderSectionHeader(section.title, section.selectedGift)}
                            {section.renderItem()}
                        </div>
                    ))}
                </div>
                {footer}
            </div>
        );
    }
);

export const CartGiftsSelectorWidgetContents = CartGiftsSelectorWidgetContentsInner;

export default CartGiftsSelectorWidgetContents;
